import { Components, createTheme, Theme } from '@mui/material/styles';

const font = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';
// Preserve the application's v4 control defaults while using the v6 components.
const components = (base: Theme): Components<Theme> => ({
  MuiTextField: { defaultProps: { variant: 'standard' as const } },
  MuiFormControl: { defaultProps: { variant: 'standard' as const } },
  MuiCssBaseline: { styleOverrides: { body: base.typography.body2 } },
  MuiAccordionSummary: {
    styleOverrides: {
      expandIconWrapper: {
        padding: parseFloat(base.spacing(1.5)),
        marginRight: -parseFloat(base.spacing(1.5)),
        fontSize: base.typography.pxToRem(24),
      },
    },
  },
  MuiCheckbox: { defaultProps: { color: 'secondary' as const } },
  MuiRadio: { defaultProps: { color: 'secondary' as const } },
  MuiSwitch: { defaultProps: { color: 'secondary' as const } },
  MuiTabs: { defaultProps: { indicatorColor: 'secondary' as const, textColor: 'inherit' as const } },
  MuiLink: { defaultProps: { underline: 'hover' as const } },
  MuiAppBar: { defaultProps: { enableColorOnDark: true } },
  MuiTooltip: {
    defaultProps: { disableInteractive: true },
    styleOverrides: { tooltip: { fontSize: base.typography.pxToRem(10), lineHeight: '1.4em' } },
  },
  MuiButton: {
    defaultProps: { color: 'inherit' },
    styleOverrides: {
      containedInherit: {
        backgroundColor: base.palette.grey[300],
        color: base.palette.getContrastText(base.palette.grey[300]),
        '&:hover': { backgroundColor: base.palette.grey.A100 },
      },
      outlinedInherit: { borderColor: base.palette.mode === 'dark' ? 'rgba(255,255,255,0.23)' : 'rgba(0,0,0,0.23)' },
    },
  },
  MuiIconButton: {
    styleOverrides: {
      root: { padding: parseFloat(base.spacing(1.5)) },
      sizeSmall: { padding: parseFloat(base.spacing(0.375)) },
      sizeLarge: { fontSize: '1.5rem' },
    },
  },
  MuiDialogContent: {
    styleOverrides: {
      root: {
        padding: base.spacing(1, 3),
        '&:first-child': { paddingTop: parseFloat(base.spacing(2.5)) },
        '.MuiDialogTitle-root + &': { paddingTop: parseFloat(base.spacing(1)) },
      },
      dividers: {
        padding: base.spacing(2, 3),
        '.MuiDialogTitle-root + &': { paddingTop: parseFloat(base.spacing(2)) },
      },
    },
  },
  MuiTab: {
    styleOverrides: {
      root: {
        minWidth: parseFloat(base.spacing(9)),
        maxWidth: parseFloat(base.spacing(33)),
        padding: base.spacing(0.75, 1.5),
        lineHeight: 1.75,
        [base.breakpoints.up('sm')]: { minWidth: parseFloat(base.spacing(20)) },
      },
      labelIcon: {
        paddingTop: parseFloat(base.spacing(1.125)),
        paddingBottom: parseFloat(base.spacing(0.75)),
      },
    },
  },
  MuiTableCell: {
    styleOverrides: {
      sizeSmall: {
        padding: base.spacing(0.75, 3, 0.75, 2),
        '&:last-child': { paddingRight: parseFloat(base.spacing(2)) },
      },
    },
  },
});
const light = function createMyLightTheme(options?: any) {
  const base = createTheme({
    ...options,
    breakpoints: { values: { xs: 0, sm: 600, md: 960, lg: 1280, xl: 1920 } },
    typography: { fontFamily: font, },
    palette: {
      mode: 'light',
      primary: {
        main: '#263238',
        dark: '#000A12',
        light: '#4F5B62',
        contrastText: '#FFFFFF',
        darker: '#000000DE',
        500: '#607D8B'
      },
      secondary: {
        main: '#FB8C00',
        dark: '#ff6d00',
        light: '#4F5B62',
        contrastText: '#00000087',
        background: '#FFDFB2',
      },
      error: {
        main: '#F44336',
        dark: '#BA000D',
        light: '#FF7961',
        contrastText: '#00000087',
      },
      warning: {
        main: '#FDD835',
        dark: '#C6A700',
        light: '#FFFF6B',
        contrastText: '#00000087',
      },
      success: {
        main: '#4CAF50',
        dark: '#087F23',
        light: '#80E27E',
        contrastText: '#00000087',
      },
      info: {
        main: '#2196F3',
        dark: '#0069C0',
        light: '#6EC6FF',
        contrastText: '#00000087',
      },
      icon: { accent: '#FFF3E0' },
      grey: {
        50: '#FAFAFA',
        100: '#F5F5F5',
        200: '#EEEEEE',
        300: '#E0E0E0',
        400: '#BDBDBD',
        500: '#9E9E9E',
        600: '#757575',
        700: '#616161',
        800: '#424242',
        900: '#212121',
      },
      text: {
        primary: '#000000',
        secondary: '#000000',
        disabled: '#00000030',
        hint: '#00000020',
        shadow: '#00000073',
        light: '#78909C',
        primary_light: '#90A4AE',
      },
      dualText: {
        primary: '#FFFFFF',
        secondary: '#FFFFFF45',
        disabled: '#FFFFFF30',
        hint: '#FFFFFF20',
      },
      divider: '#D1B74F20',
      neutraldivider: '#D3D7D9',
      background: {
        paper: '#FFFFFF',
        default: '#ECEFF1',
        ligth: '#FFFBEA',
        sidebar: '#FFFFFFDE',
        component: '#B0BEC5',
        paperTranslucent: '#ffffff5f',
      },
      table: {
        hover: '#d5dbdf',
        row_left: '#90A4AE',
        row_bottom: '#00000029'
      },
      dualBackground: {
        paper: '#263238',
        default: '#263238',
      },
      offBackground: {
        paper: '#263238',
        default: '#FFFFFF'
      },
      common: {
        black: '#000000',
        white: '#FFFFFF',
      },
      button: { default: '#CFD8DC', hover: '#FFBD45' },
      tab: { offselected: '#607D8B' },
      border: {
        dashed: '#707070',
        main: '#00000033',
        line: '#D1D8DC',
        card: '#D1D8DB',
      },
      input: { border: '#0000001A' }
    },
    accent: {
      main: '#D1B74F',
      dark: '#9D871E',
      light: '#FFE97F',
      accent: '#00000087',
    },
  });
  return createTheme(base, { components: components(base) });
};
const dark = function createMyDarkTheme(options?: any) {
  const base = createTheme({
    ...options,
    breakpoints: { values: { xs: 0, sm: 600, md: 960, lg: 1280, xl: 1920 } },
    typography: { fontFamily: font, },
    palette: {
      mode: 'dark',
      primary: {
        main: '#263238',
        dark: '#000A12',
        light: '#4F5B62',
        contrastText: '#FFFFFF',
        darker: '#000000DE',
      },
      secondary: {
        main: '#ff6d00',
        dark: '#FB8C00',
        light: '#4F5B62',
        contrastText: '#000000DE',
      },
      icon: { accent: '#FFF3E0' },
      error: {
        main: '#F44336',
        dark: '#BA000D',
        light: '#FF7961',
        contrastText: '#FFFFFF',
      },
      warning: {
        main: '#FDD835',
        dark: '#C6A700',
        light: '#FFFF6B',
        contrastText: '#00000087',
      },
      success: {
        main: '#4CAF50',
        dark: '#4CAF50',
        light: '#80E27E',
        contrastText: '#00000087',
      },
      info: {
        main: '#2196F3',
        dark: '#0069C0',
        light: '#6EC6FF',
        contrastText: '#00000087',
      },
      grey: {
        50: '#FAFAFA',
        100: '#F5F5F5',
        200: '#EEEEEE',
        300: '#E0E0E0',
        400: '#BDBDBD',
        500: '#9E9E9E',
        600: '#757575',
        700: '#616161',
        800: '#424242',
        900: '#212121',
      },
      text: {
        primary: '#FFFFFFDE',
        secondary: '#FFFFFFB3',
        disabled: '#FFFFFF8A',
        hint: '#FFFFFF20',
        shadow: '#FFFFFF99',
        light: '#90A4AE',
        primary_light: '#B0BEC5',
      },
      dualText: {
        primary: '#FFFFFF',
        secondary: '#FFFFFF',
        disabled: '#FFFFFF',
        hint: '#FFFFFF',
      },
      action: { disabled: '#FFFFFF8A' },
      divider: '#D1B74F',
      neutraldivider: '#D3D7D9',
      background: {
        paper: '#263238',
        default: '#000A12',
        ligth: '#FFFBEA',
        paperTranslucent: 'rgba(38,50,56,0.37)',
        component: '#37474F',
      },
      dualBackground: {
        paper: '#263238',
        default: '#263238',
      },
      offBackground: {
        paper: '#FFFFFF',
        default: '#263238'
      },
      common: {
        black: '#000000',
        white: '#FFFFFF',
      },
      button: { default: '#CFD8DC', hover: '#FFBD45' },
      tab: { offselected: '#B0BEC5' },
      border: { dashed: '#707070', main: '#FFFFFF33', line: '#D1D8DC' },
      input: { border: '#FFFFFF1A' },
      table: {
        hover: '#37474F',
        row_left: '#90A4AE',
        row_bottom: '#FFFFFF29',
      },
    },
    accent: {
      main: '#D1B74F',
      dark: '#9D871E',
      light: '#FFE97F',
      accent: '#00000087',
    },
  });
  return createTheme(base, {
    components: {
      ...components(base),
      MuiPaper: { styleOverrides: { root: { backgroundImage: 'none' } } },
      MuiAvatar: {
        styleOverrides: {
          colorDefault: { backgroundColor: base.palette.button.default, color: base.palette.primary.main },
        },
      },
    },
  });
};

export default { light, dark };
